import React, { useState } from 'react';
import { useTheme, Box, Container, Typography, List, ListItem, ListItemText, Divider, Stack, Paper, Link, Tooltip,
          Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert } from '@mui/material';

import EtlImg from '../../../../assets/images/etl_pipeline.png'; 
import AWSImg from '../../../../assets/images/aws_img.png'; 
import GlueImg from '../../../../assets/images/glue_logo.png';
import CrawlerImg from '../../../../assets/images/glue_logo.png';
import GlueJobImg from '../../../../assets/images/glue_job_logo.png';
import StepFImg from '../../../../assets/images/step_f_logo.png';
import SchoolIcon from '@mui/icons-material/School';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon, CloudDone as CloudDoneIcon,  Check as CheckIcon, FileDownload as FileDownloadIcon, Cached as CachedIcon, CloudUpload as CloudUploadIcon, Grading as GradingIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { GridArrowUpwardIcon, GridArrowDownwardIcon, GridRemoveIcon, GridAddIcon, GridSearchIcon, GridFilterListIcon } from '@mui/x-data-grid-pro';
import { CalendarIcon } from '@mui/x-date-pickers';
import EtlDataStagesImg from '../../../../assets/images/etl_stage_status_map.png';
interface HelpSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

export default function HelpView() {

  const theme = useTheme();

  const scrollToSection = (id: string) => {    
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const headerStyle = {
    paddingBottom: 0,
    paddingTop: 4
  };
  
  const listItemStyle = {
    marginBottom: '10px'
  };
  
  const tableCellBordersStyle = {
    borderBottom: `1px solid ${theme.palette.grey[300]}`, 
    borderRight: `1px solid ${theme.palette.grey[300]}`, 
  };
  
  const renderBackToTop = () => {
  
    const scrollToTop = () => {
      const topElement = document.getElementById('top');
  
      if (topElement) {
        topElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };
  
    return (
      <Box sx={{ textAlign: 'left', mt: 2 }}>
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            scrollToTop();
          }}
        >
          <Typography variant="body2" sx={{ textDecoration: 'underline', mt:4 }}>
            Back to Top
          </Typography>
        </Link>
      </Box>
    );
  };

  const renderEtl101Section = () => {
    return (
      <Stack spacing={3} sx={{ py: 2 }}>
  
        <Typography variant='h6' sx={headerStyle}>
          What is an ETL Pipeline?
        </Typography>
  
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          An ETL (Extract, Transform, Load) Data Pipeline is a systematic process for collecting,
          processing, and transferring data from various sources to a destination, typically a data lake or data warehouse.
          This powerful framework enables organizations to efficiently manage and analyze large volumes of data,
          making informed decisions based on valuable insights.
        </Typography>
  
        <Typography variant="h6" sx={headerStyle}>
          ETL Pipeline: Components
        </Typography>
  
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          1. <strong>Extract:&nbsp;</strong> In this phase, data is gathered from diverse sources, such as databases,
          applications, logs, or external APIs. Extraction involves pulling raw data in its original format,
          preserving its integrity during transfer.
        </Typography>
  
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          2. <strong>Transform:&nbsp;</strong> 
          The extracted data undergoes transformations to meet the requirements of the target system or database.
          Cleaning, filtering, aggregating, and restructuring are common transformation tasks.
          This phase ensures data consistency and enhances its quality for analytics and reporting.
        </Typography>
  
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          3. <strong>Load:&nbsp;</strong> 
          Transformed data is loaded into a centralized repository, often a data warehouse, where it can be easily accessed and analyzed.
          Loading may involve different strategies, such as batch loading or real-time streaming, depending on business needs.
        </Typography>
  
        <Typography component="img" src={EtlImg} alt="Example ETL Pipeline" sx={{ width: '80%', height: 'auto', py: 4 }} />
  
        <Typography variant="h6" sx={headerStyle}>
          ETL Pipeline: Key Concepts
        </Typography>
  
        <TableContainer component={Paper} sx={{ maxWidth: 600, margin: 'auto' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell><strong>Data Integrity</strong></TableCell>
                <TableCell>ETL pipelines prioritize maintaining data accuracy and consistency throughout the process.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Scalability</strong></TableCell>
                <TableCell>ETL pipelines should be designed to handle growing data volumes efficiently.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Scheduling</strong></TableCell>
                <TableCell>Regular, automated execution of ETL processes is essential to ensure up-to-date information in the data warehouse.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Monitoring and Logging</strong></TableCell>
                <TableCell>Implementing monitoring tools and logging mechanisms helps identify and address issues promptly.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
  
        <Typography variant="h6" sx={headerStyle}>
          ETL Pipeline: Benefits
        </Typography>
  
        <TableContainer component={Paper} sx={{ maxWidth: 600, margin: 'auto' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell><strong>Consistency</strong></TableCell>
                <TableCell>ETL ensures data consistency across different sources, reducing discrepancies and improving overall data quality.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Time Efficiency</strong></TableCell>
                <TableCell>Automation streamlines the data processing workflow, saving time compared to manual data handling.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Improved Decision-Making</strong></TableCell>
                <TableCell>ETL pipelines enable businesses to make informed decisions by providing a centralized and reliable source of data.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        
        <Typography variant="h6" sx={headerStyle}>
          Data Storage In The Cloud
        </Typography>
  
        <TableContainer sx={{ border: 'none'}}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top', margin: 0, padding: 0, border: 'none' }}>
                  <Typography component="div">
                    The landscape of data warehousing and analytics has been transformed by:
                    <ul>
                      <li style={listItemStyle}>Continued drop in storage and compute costs in the cloud</li>
                      <li style={listItemStyle}>Emergence of highly scalable, columnar databases</li>
                      <li style={listItemStyle}>Ease of building and deploying data pipelines, data lakes, warehouses, and analytics processing in the cloud</li>
                    </ul>
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 0}}>
                  <Typography component="div">
                    <strong>Data Lake</strong>
                    <ul>
                      <li style={listItemStyle}>Repository that stores all organizational data in it&apos;s natural, raw state</li>
                      <li style={listItemStyle}>Structured, semi structured, or even unstructured</li>
                      <li style={listItemStyle}>A broader range of data can be analyzed in new ways to gain unexpected and previously unavailable insights</li>
                    </ul>
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', border: 'none', margin: 0, padding: 0 }}>
                  <Typography component="div">
                    <strong>Data Warehouse</strong>
                    <ul>
                      <li style={listItemStyle}>Repository that stores data accumulated from a wide range of operational and external data sources</li>
                      <li style={listItemStyle}>Data is structured, filtered, and already processed for a specific purpose</li>
                      <li style={listItemStyle}>Optimized for reporting and analysis queries</li>
                    </ul>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
   
        <Typography id="quality_stages" variant="h6" sx={headerStyle}>
          Data Quality Stages
        </Typography>
  
        <TableContainer component={Paper} sx={{ margin: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#B4D86C' }}>
                <TableCell>
                  <Typography variant="subtitle2" style={{ color: 'white' }}>Stage</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" style={{ color: 'white' }}>Data</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" style={{ color: 'white' }}>Format(s)</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" style={{ color: 'white' }}>Description</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={ tableCellBordersStyle }><strong>Bronze</strong></TableCell>
                <TableCell sx={ tableCellBordersStyle }>Raw</TableCell>
                <TableCell sx={ tableCellBordersStyle }>csv, xlsx, json, xml</TableCell>
                <TableCell sx={ tableCellBordersStyle } style={{ borderRight: 0 }}>
                  <ul>
                    <li style={listItemStyle}>Stores and catalogs <strong>raw data</strong> from source systems</li>
                    <li style={listItemStyle}>Data is as close to original form as possible</li>
                    <li style={listItemStyle}>Not normalized, formatted, or modeled</li>
                    <li style={listItemStyle}>Provides replayability</li>
                  </ul>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={ tableCellBordersStyle }><strong>Silver</strong></TableCell>
                <TableCell sx={ tableCellBordersStyle }>Refined</TableCell>
                <TableCell sx={ tableCellBordersStyle }>parquet, SQL, NoSQL</TableCell>
                <TableCell sx={ tableCellBordersStyle } style={{ borderRight: 0 }}>
                  <ul>
                    <li style={listItemStyle}>Stores results of raw data being cleaned, formatted, and normalized</li>
                    <li style={listItemStyle}>Fine-grained data, without aggregations</li>
                    <li style={listItemStyle}>Column names and data type inconsistencies have been corrected, data and time fields normalized, and data quality has been validated and fixed to the extent possible</li>
                    <li style={listItemStyle}><strong>Core schema + client schema</strong></li>
                  </ul>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={ tableCellBordersStyle }><strong>Gold</strong></TableCell>
                <TableCell sx={ tableCellBordersStyle }>Curated</TableCell>
                <TableCell sx={ tableCellBordersStyle }>parquet, SQL, NoSQL</TableCell>
                <TableCell sx={ tableCellBordersStyle } style={{ borderRight: 0 }}>
                  <ul>
                    <li style={listItemStyle}>Data stored in optimized format, used to derive meaningful insight</li>
                    <li style={listItemStyle}><strong>Modeling</strong></li>
                  </ul>
                </TableCell>
              </TableRow>
             
            </TableBody>
          </Table>
        </TableContainer>
  
  
  
      </Stack>
    );
  };

  const renderAwsGlueEtlSection = () => {
    return (
      <Stack spacing={3} sx={{ py: 2 }}>
  
        <Typography variant='h6' sx={headerStyle}>
          What is AWS?
        </Typography>
  
        <Container component='div' sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="body1" sx={{ marginBottom: 2 }}>
            Amazon Web Services (AWS) is a comprehensive cloud computing platform offered by Amazon.
            It provides a wide range of on-demand computing services, including computing power, storage, databases, machine learning, analytics, and more.
            AWS allows businesses to access and utilize these resources over the internet, eliminating the need for physical infrastructure and providing scalable solutions. 
            In simpler terms, AWS enables organizations to run applications and store data in the cloud, fostering agility, cost-effectiveness, and innovation without the complexities of managing traditional IT infrastructure.
          </Typography>
          <Typography component="img" src={AWSImg} alt="AWS Glue" sx={{ width: '200px', height: 'auto', marginLeft: 8, marginRight: 10 }} />
        </Container>

        <Typography variant='h6' sx={headerStyle}>
          What is AWS Glue?
        </Typography>
  
        <Typography variant="body1" component='div' sx={{ marginBottom: 2 }}>
          <ul>
            <li style={listItemStyle}>AWS Glue is a modern, serverless ETL data integration service</li>        
            <li style={listItemStyle}>Discover, prepare, move, and integrate data from multiple sources for analytics, machine learning (ML), and application development</li>
            <li style={listItemStyle}>No infrastructure to manage</li>
          </ul>
        </Typography>

        <Typography variant='h6' sx={headerStyle}>
          AWS Glue: Benefits
        </Typography>

        <Typography variant="body1" component='div' sx={{ marginBottom: 2 }}>
          <ul>
            <li style={listItemStyle}>High availability</li>
            <li style={listItemStyle}>Auto-scaling (scale on demand)</li>            
            <li style={listItemStyle}>Centralized cataloging</li>
            <li style={listItemStyle}>Distributed processing for big data workloads (based on Apache Spark)</li>
            <li style={listItemStyle}>Pay-as-you-go billing</li>
          </ul>
        </Typography>

        <Typography variant='h6' sx={headerStyle}>
          AWS Glue: Key Features
        </Typography>

        <TableContainer sx={{ border: 'none', paddingTop: 2 }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top', margin: 0, padding: 0, border: 'none' }}>
                  <Typography component="img" src={GlueImg} alt="AWS Glue" sx={{ width: '50px', height: 'auto' }} />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 0}}>
                  <Typography component="div">
                    <strong>Data catalog</strong>
                    <ul>
                      <li style={listItemStyle}>Persistent meta-store for all data assets</li>
                      <li style={listItemStyle}>Contains table definitions, job definitions, and schemas</li>
                      <li style={listItemStyle}>All data assets accessible from analytics tool of choice</li>
                    </ul>
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', margin: 0, padding: 0, border: 'none' }}>
                  <Typography component="img" src={CrawlerImg} alt="AWS Glue" sx={{ width: '50px', height: 'auto' }} />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 0}}>
                  <Typography component="div">                    
                    <strong>Crawlers and classifiers</strong>
                    <ul>
                      <li style={listItemStyle}>Automatic schema discovery</li>
                      <li style={listItemStyle}>Creates metadata tables in Data Catalog</li>
                      <li style={listItemStyle}>Maintains schema version history (how has data changed over time?)</li>
                    </ul>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top', margin: 0, padding: 0, border: 'none' }}>
                  <Typography component="img" src={GlueJobImg} alt="AWS Glue" sx={{ width: '50px', height: 'auto' }} />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 0}}>
                  <Typography component="div">
                    <strong>Jobs</strong>
                    <ul>
                      <li style={listItemStyle}>Composed of data source(s), transformation script(s), and data target(s)</li>
                      <li style={listItemStyle}>Transformation business logic</li>
                      <li style={listItemStyle}>Initiated by triggers or scheduled</li>
                    </ul>
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', margin: 0, padding: 0, border: 'none' }}>
                  <Typography component="img" src={StepFImg} alt="AWS Glue" sx={{ width: '50px', height: 'auto' }} />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 0}}>
                  <Typography component="div">                    
                    <strong>Workflow orchestration</strong>
                    <ul>
                      <li style={listItemStyle}>Manage and sequence the execution of jobs, crawlers, and triggers (and other AWS resources)</li>
                      <li style={listItemStyle}>This is the ETL pipeline!</li>
                      <li style={listItemStyle}>Job time-out and retry handling</li>
                      <li style={listItemStyle}>Logging and alerts</li>
                    </ul>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>   

        <Typography variant='h6' sx={headerStyle}>
          AWS Glue: Jobs
        </Typography>   

        <TableContainer sx={{ border: 'none', paddingTop: 2 }}>
          <Table>
            <TableBody>
              <TableRow sx={{ borderTop: `1px solid ${theme.palette.grey[300]}`, borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                <TableCell sx={{ width: '60%', verticalAlign: 'top', margin: 0, padding: 0, border: 'none', py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'light' }}>AWS Glue ETL Jobs</Typography>
                </TableCell>
                <TableCell sx={{ width: '40%', verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 2}}>
                  <Typography variant="h6" sx={{ fontWeight: 'light' }}>AWS DataBrew Jobs</Typography>        
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                <TableCell sx={{ verticalAlign: 'top', margin: 0, padding: 0, paddingRight: 4, border: 'none', borderRight: `1px solid ${theme.palette.grey[300]}` }}>
                  <Table sx={{ marginTop: 2 }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: '50%', verticalAlign: 'top', margin: 0, padding: 0, border: 'none', paddingBottom: 4 }}>                          
                          <Typography variant="body1" component='div' sx={{ marginBottom: 2 }}>
                            <strong>PySpark Script Jobs</strong>
                            <ul>
                              <li style={listItemStyle}>Spark clustering and parallel processing environment</li>
                              <li style={listItemStyle}>Provides Glue DynamicFrame and Spark DataFrame for powerful, flexible transformations</li>
                              <li style={listItemStyle}>Enables bookmark feature</li>
                            </ul>
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: '50%', verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 0}}>                          
                          <Typography variant="body1" component='div' sx={{ marginBottom: 2 }}>
                            <strong>Python Shell Script Jobs</strong>
                            <ul>
                              <li style={listItemStyle}>Simple Python scripts</li>
                              <li style={listItemStyle}>No Spark clustering</li>
                              <li style={listItemStyle}>Pandas and Numpy available</li>
                            </ul>
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'top', margin: 0, padding: 0, border: 'none' }}>                        
                          <Typography variant="body1" component='div' sx={{ marginBottom: 2 }}>
                            <strong>Visual Designer Jobs</strong>
                            <ul>
                              <li style={listItemStyle}>No-code visual designer</li>
                              <li style={listItemStyle}>Generates PySpark script</li>
                            </ul>
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 0}}>                        
                          <Typography variant="body1" component='div' sx={{ marginBottom: 2 }}>
                            <strong>Jupyter Notebook Jobs</strong>
                            <ul>
                              <li style={listItemStyle}>Interactive notebook</li>
                              <li style={listItemStyle}>Debug data in real-time</li>
                              <li style={listItemStyle}>Data exploration</li>
                            </ul>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 0 , paddingLeft: 4}}>
                  <Typography variant="body1" component='div' sx={{ paddingBottom: 14, marginTop: 2 }}>
                    <strong>Data Profile Jobs</strong>
                    <ul>
                      <li style={listItemStyle}>Provides descriptive statistics for full dataset</li>
                      <li style={listItemStyle}>Attach and run data quality rules</li>
                    </ul>
                  </Typography>
                  <Typography variant="body1" component='div' sx={{ }}>
                    <strong>Recipe Jobs</strong>
                    <ul>
                      <li style={listItemStyle}>Generate PySpark script from DataBrew recipes</li>
                    </ul>
                  </Typography>         
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>   

        <Typography variant='h6' sx={headerStyle}>
          AWS Glue: Managing Data Quality
        </Typography>     

        <TableContainer sx={{ border: 'none', paddingTop: 2 }}>
          <Table>
            <TableBody>
              <TableRow sx={{ }}>
                <TableCell sx={{ width: '40%', verticalAlign: 'top', margin: 0, padding: 0, border: 'none', py: 2 }}>
                  <Typography variant="body1" component='div'>
                    <strong>Problem</strong>
                    <ul>
                      <li style={listItemStyle}>Incoming data contains incorrect, missing, and malformed data</li>
                      <li style={listItemStyle}>Hard to evaluate quality of data without automated mechanism</li>
                      <li style={listItemStyle}>Upstream errors cause downstream problems</li>
                    </ul>
                  </Typography>
                  <Typography variant="body1" component='div'>
                    <strong>Solution</strong>
                    <ul>
                      <li style={listItemStyle}>Manage data quality by applying predefined rules and verifying if the data meets those criteria or not</li>
                      <li style={listItemStyle}>Build Data Quality Checks into pipeline workflows</li>
                      <li style={listItemStyle}>Take corrective action at first point of failure</li>
                    </ul>
                  </Typography> 
                </TableCell>
                <TableCell sx={{ width: '60%', verticalAlign: 'top', border: 'none', margin: 0, px: 4, py: 2}}>
                  <Table sx={{ marginTop: 2 }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ width: '50%', verticalAlign: 'top', border: `1px solid ${theme.palette.grey[300]}` }}>
                          <Typography component='p' sx={{ marginBottom: 2 }}><strong>Data Quality</strong></Typography>
                          <Typography component='p' sx={{ marginBottom: 2 }}>Describes how well a dataset serves its specific purpose</Typography>
                          <Typography component='p' sx={{ marginBottom: 2 }}>Data quality score used to quantify</Typography>
                        </TableCell>
                        <TableCell sx={{ width: '50%', verticalAlign: 'top', border: `1px solid ${theme.palette.grey[300]}`}}>                          
                          <Typography component='p' sx={{ marginBottom: 2 }}><strong>DQDL</strong></Typography>
                          <Typography component='p' sx={{ marginBottom: 2 }}>(Data Quality Definition Language)</Typography>
                          <Typography component='p' sx={{ marginBottom: 2 }}>A domain-specific language used to write Data Quality Rules</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'top', border: `1px solid ${theme.palette.grey[300]}`}}>                          
                          <Typography component='p' sx={{ marginBottom: 2 }}><strong>Rule</strong></Typography>
                          <Typography component='p' sx={{ marginBottom: 2 }}>A DQDL expression that checks your data for a specific characteristic and returns a Boolean value</Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', border: `1px solid ${theme.palette.grey[300]}`}}>                          
                          <Typography component='p' sx={{ marginBottom: 2 }}><strong>Ruleset</strong></Typography>
                          <Typography component='p' sx={{ marginBottom: 2 }}>A set of data quality rules</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ verticalAlign: 'top', border: `1px solid ${theme.palette.grey[300]}`}}>                          
                          <Typography component='p' sx={{ marginBottom: 2 }}><strong>Data Quality Score</strong></Typography>
                          <Typography component='p' sx={{ marginBottom: 2 }}>The percentage of passing data quality rules when you evaluate a ruleset</Typography>
                        </TableCell>
                        <TableCell sx={{  verticalAlign: 'top', border: `1px solid ${theme.palette.grey[300]}`}}>                          
                          &nbsp;
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

      </Stack>
    );
  };

  const renderAmaGettingStarted = () => {

    const bronzeStatusItems = [
      { status: 'BRONZE_COLLECTING', description: 'Data is currently being collected.' },
      { status: 'BRONZE_COLLECTED', description: 'Data has been successfully collected.' },
      { status: 'BRONZE_COLLECT_ERROR', description: 'An error occurred during data collection.' },
      { status: 'BRONZE_INGESTING', description: 'Data is currently being ingested.' },
      { status: 'BRONZE_INGESTED', description: 'Data has been successfully ingested.' },
      { status: 'BRONZE_INGEST_ERROR', description: 'An error occurred during data ingestion.' },
    ];

    const silverStatusItems = [
      { status: 'SILVER_TRANSFORMING', description: 'Data is currently being transformed.' },
      { status: 'SILVER_TRANSFORMED', description: 'Data has been successfully transformed.' },
      { status: 'SILVER_TRANSFORM_ERROR', description: 'An error occurred during data transformation.' }
    ];

    const goldStatusItems = [
      { status: 'GOLD_TRANSFORMING', description: 'Data is currently being refined.' },
      { status: 'GOLD_TRANSFORMED', description: 'Data has been successfully refined.' },
      { status: 'GOLD_TRANSFORM_ERROR', description: 'An error occurred during data refinement.' }
    ];

    const listItemStyle = {
      m: 0, p: 0, mb: 0,
    };

    return (
      <Stack spacing={3} sx={{ py: 2 }}>

        <Typography variant='h6' sx={headerStyle}>
          Introduction
        </Typography>

        <Typography variant="body1"  component='div' sx={{ pb: 4, maxWidth:'800px' }}>
          <Typography component='p' sx={{ mb: 2 }}>Our system operates on an ETL (Extract, Transform, Load) pipeline model.</Typography>
          <Typography component='p' sx={{ mb: 2 }}>Firstly, we configure <strong>Client</strong> entities. This is essentially the setup of your account in our system. Once a client is configured, we create <strong>Snapshots</strong>. These are point-in-time data collections, each associated with a specific date and linked to the client in a many-to-one relationship.</Typography>
          <Typography component='p' sx={{ mb: 2 }}>Each snapshot represents a single run through the ETL pipeline. The pipeline consists of a series of jobs for each stage, responsible for data cleansing and transformation.</Typography>
          <Typography component='p' sx={{ mb: 2 }}>Our web interface provides a user-friendly platform for managing CRUD (Create, Read, Update, Delete) operations for both clients and snapshots.</Typography>
          <Typography component='p' sx={{ mb: 2 }}>Upon the collection of files for a snapshot, the pipeline initiates execution, writing data to three stages: Bronze, Silver, and Gold. Each stage represents a different level of data processing and refinement.</Typography>
          <Typography component='p' sx={{ mb: 2 }}>The web interface also provides real-time monitoring of the pipeline status. Additionally, email notifications are dispatched at key points in the process, ensuring you&apos;re kept informed of the pipeline&apos;s progress.</Typography>
          <Typography component='p' sx={{ mb: 2 }}></Typography>          
        </Typography>


        <Typography variant='h6' sx={headerStyle}>
          Pipeline Status by Stage
        </Typography>
  
        <Typography variant="body1" sx={{ pb: 4, maxWidth:'800px' }}>
          Each stage in the AMA ETL Pipeline represents a different level of data processing and refinement.
          Status enums for each stage give you real-time insights into the data processing progress. This can be helpful for troubleshooting and monitoring.
        </Typography>

        <Typography component="img" src={EtlDataStagesImg} alt="ETL Pipeline" sx={{ maxWidth: '800px', height: 'auto' }} />
  
        <Box sx={{ paddingLeft: '60px' }}>
          <TableContainer sx={{ maxWidth: '800px', border: 0 }}>
            <Table>
              <TableBody>
                <TableRow >
                <TableCell sx={{ verticalAlign: 'top', border: 'none' }}>
                  <List>
                    {bronzeStatusItems.map((item, index) => (
                      <Tooltip title={item.description} key={index}>
                        <ListItem sx={listItemStyle}>
                          <ListItemText sx={{ m: 0, p: 0 }}>
                            <Typography variant="body2" sx={{ m: 0, p: 0 }}>{item.status}</Typography>
                          </ListItemText>
                        </ListItem>
                      </Tooltip>
                    ))}
                  </List>
                </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', border: 'none' }}>
                    <List>
                    {silverStatusItems.map((item, index) => (
                      <Tooltip title={item.description} key={index}>
                        <ListItem sx={listItemStyle}>
                          <ListItemText sx={{ m: 0, p: 0 }}>
                            <Typography variant="body2" sx={{ m: 0, p: 0 }}>{item.status}</Typography>
                          </ListItemText>
                        </ListItem>
                      </Tooltip>
                    ))}
                    </List>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', border: 'none' }}>
                    <List>
                    {goldStatusItems.map((item, index) => (
                      <Tooltip title={item.description} key={index}>
                        <ListItem sx={listItemStyle}>
                          <ListItemText sx={{ m: 0, p: 0 }}>
                            <Typography variant="body2" sx={{ m: 0, p: 0 }}>{item.status}</Typography>
                          </ListItemText>
                        </ListItem>
                      </Tooltip>
                    ))}
                    </List>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Typography variant='body1'>
          explain entities: clients, snapshots
        </Typography>
        <Typography variant='body1'>
          input and output: bronze to silver to gold
        </Typography>
        <Typography variant='body1'>
          sequencing: preprocessing, ETL workflow (explain status)
        </Typography>
        <Typography variant='body1'>
          jobs: status and notification
        </Typography>
        <Typography variant='body1'>
          Workflow diagram
        </Typography>

      </Stack>
    );
  };

  const renderAmaConfiguringClients = () => {
    return (
      <Stack spacing={3} sx={{ }}>

        <Typography id="client_add" variant='h6' sx={headerStyle}>
          Add Client
        </Typography>

        <Typography variant='body1' component='div' sx={{ lineHeight: 2 }}>
          Follow these steps to add a new client.
          <ol>
            <li>Click the <SchoolIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom', marginRight: '10px' }}/><strong>AMA</strong> menu item to navigate to the AMA dashboard.</li>
            <li>Click the <strong>Clients</strong> tab to navigate to the Clients grid.</li>
            <li>Click the <AddCircleIcon color="primary" aria-label="Add Client" sx={{verticalAlign: 'text-bottom', marginRight: '10px'}} />floating action button (bottom right of grid) to open the Client detail view.</li>
            <li>Add client details:
              <ul>
                  <li><strong>Client ID</strong>: (required) Primary key for client records. Only lowercase alpha characters and underscores permitted. ex) u_of_hcrc </li>
                  <li><strong>Display name</strong>: (required) Full client display name used for reporting purposes. ex) University of HCRC</li>
                  <li><strong>Pipeline description</strong>: (optional) Description of pipeline.</li>
                  <li><strong>Notification recipients</strong>: (optional) List of email recipients (csv) that will receive ETL pipeline notifications. ex) email1@humancapital.com, email2@humancapital.com</li>
              </ul>
            </li>
            <li>Configure manifest:
              <Alert severity="info" sx={{ my: 2, maxWidth: '600px' }}>The manifest defines the input files required to be collected for every snapshot. A manifest item includes a <em>file label</em> and a <em>csv string</em> identifying PII attributes. At least one manifest item is required.</Alert>
              <ol>
                  <li>Click <AddBoxIcon aria-label="Add Icon" sx={{verticalAlign: 'text-bottom' }}/> button to add new manifest items. Click <DeleteIcon sx={{verticalAlign: 'text-bottom' }} aria-label="Delete Icon" /> to the right of an existing manifest item to delete it.</li>
                  <li><strong>File label</strong>: (required) Label used to uniquely identify each required source file (manifest item) ex) fin_aid or apps or common</li>
                  <li><strong>File attributes PII list</strong>: (optional) List (csv) of file attributes that contain PII data. This must exactly match columns in source file and is case sensitive.  ex) first_name, middle, last_name </li>
              </ol>
            </li>
            <li>
              Click <strong>SAVE</strong> to save the client and return to clients list view (or click <strong>CANCEL</strong> to discard changes).
              <Alert severity="info" sx={{ my: 2, maxWidth: '600px' }}>When you save a client configuration, back-end AWS resources are being dynamically provisioned and a new ETL pipeline is created!</Alert>
            </li>
          </ol>
          
        </Typography>

        <Typography variant='h6' sx={headerStyle}>
          Edit Client
        </Typography>

        <Typography variant='body1' component='div' sx={{ lineHeight: 2 }}>
          Follow these steps to edit an existing client (for example, adding recipients to the notification list).
          <ol>
            <li>Click the <SchoolIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom', marginRight: '10px' }}/><strong>AMA</strong> menu item to navigate to the AMA dashboard.</li>
            <li>Click the <strong>Clients</strong> tab to navigate to the Clients grid.</li>
            <li>Click the <EditIcon aria-label="Edit Icon" sx={{verticalAlign: 'text-bottom' }}/> button on the client row you want to edit.</li>
            <li>Update client configuration as needed.</li>
            <li>Click <strong>SAVE</strong> to save the client changes and return to clients list view (or click <strong>CANCEL</strong> to discard changes).</li>
          </ol>
        </Typography>

        <Typography variant='h6' sx={headerStyle}>
          Delete Client
        </Typography>

        <Typography variant='body1' component='div' sx={{ lineHeight: 2 }}>
          Follow these steps to delete an existing client.
          <ol>
            <li>Click the <SchoolIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom', marginRight: '10px' }}/><strong>AMA</strong> menu item to navigate to the AMA dashboard.</li>
            <li>Click the <strong>Clients</strong> tab to navigate to the Clients grid.</li>
            <li>Click the <DeleteIcon sx={{verticalAlign: 'text-bottom' }} aria-label="Delete Icon" /> button on the client row you want to delete.</li>
            <li>
              Click <strong>OK</strong> on the modal confirmation window.
              <Alert severity="warning" sx={{ my: 2, maxWidth: '600px' }}>Be very careful when deleting clients (this should be a very rare occurrence). <strong>All related snapshots and data will be permanently deleted.</strong></Alert>
            </li>
          </ol>
        </Typography>
       
      </Stack>
    );
  };

  const renderAmaManagingSnapshots = () => {
    return (
      <Stack spacing={3} sx={{ }}>
        <Typography variant='h6' sx={headerStyle}>
          Snapshots Overview
        </Typography>

        <Typography variant='body1' component='div' sx={{ lineHeight: 2 }}>
          Snapshots are a collection of files from a specific date for a particular client. On the Snapshots tab, you are able to add a new snapshot; upload additional files to an existing snapshot; delete a snapshot; and download Silver transformed data from a completed snapshot.
          <ol>
            <li>Click the <SchoolIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom', marginRight: '10px' }}/><strong>AMA</strong> menu item to navigate to the AMA dashboard.</li>
            <li>View the list of existing snapshots on the <strong>Snapshots</strong> tab.  Each row corresponds with an existing snapshot and provides information about its current status.
            <ul>
              <li><strong>Client ID</strong>: The ID of the client associated with the snapshot</li>
              <li><strong>Snapshot Date</strong>: The date of the snapshot</li>
              <li><strong>Files Collected</strong>: Indicates whether file collection for the snapshot is complete<br /><CloseIcon aria-label="X icon" sx={{verticalAlign: 'text-bottom'}}/> means that at least one file defined in the client&apos;s manifest is missing.<br /><CheckIcon aria-label="Checkmark icon" sx={{verticalAlign: 'text-bottom'}}/> means that all files defined in the client&apos;s manifest are uploaded.</li>
              <li><strong>Upload</strong>: Contains the <CloudUploadIcon aria-label="Upload files to bronze" sx={{verticalAlign: 'text-bottom'}}/> button to upload files to an existing snapshot (see <Link href="#snapshot_add_files"><Typography variant="body2" sx={{ textDecoration: 'underline' }} component="span">
              Add Files to Existing Snapshot
          </Typography></Link> below)</li>
              <li><strong>ETL Status</strong>: Indicates the current <Link href="#quality_stages">
          <Typography variant="body2" sx={{ textDecoration: 'underline' }} component="span">
            ETL quality stage
          </Typography>
        </Link> of the snapshot</li>
              <li><strong>Silver</strong>: Contains the <FileDownloadIcon aria-label="Download Silver File" sx={{verticalAlign: 'text-bottom'}}/> button to download the Silver transformed file (CSV) if the snapshot&apos;s status is <i>SILVER_TRANSFORMED</i></li>
              <li><strong>Data Quality</strong>: Contains the <GradingIcon aria-label="View Silver Data Quality Report" sx={{verticalAlign: 'text-bottom'}}/> button to view the Data Quality report if the snapshot&apos;s status is <i>SILVER_TRANSFORMED</i></li>
              <li><strong>Delete</strong>: Contains the action button to delete a snapshot (see <Link href="#snapshot_delete"><Typography variant="body2" sx={{ textDecoration: 'underline'}} component="span">
              Delete Snapshot
          </Typography></Link> below)</li>
              <li>Click <GridAddIcon aria-label="Expand Icon" sx={{verticalAlign: 'text-bottom'}}/> to expand the row to view file collection details for each file defined in the manifest.
                <ul>
                  <li><strong>File</strong>: The manifest item</li>
                  <li><strong>Error</strong>: Details of file errors (if applicable)</li>
                  <li><strong>File Collected</strong>: Indicates whether the file was collected<br /><CloseIcon aria-label="X icon" sx={{verticalAlign: 'text-bottom'}}/> means that the file is missing.<br /><CheckIcon aria-label="Checkmark icon" sx={{verticalAlign: 'text-bottom'}}/> means that the file was successfully uploaded.</li>
                  <li><strong>Status</strong>: The status of the file in the pipeline</li>
                  <li><strong>Bronze</strong>: Contains the <FileDownloadIcon aria-label="Download Bronze File"sx={{verticalAlign: 'text-bottom'}} /> button to download the file that was uploaded</li>
                </ul>
              </li>
              <li>While expanded, click <GridRemoveIcon aria-label="Collapse Icon" sx={{verticalAlign: 'text-bottom'}}/> to collapse the row.</li>
            </ul>
            </li>
            <li>Search for specific snapshots:
             <ul>
                <li>Use the <GridSearchIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom'}}/> Search box on the top right of the table to search by typing the name of a client.</li>
              
              <li>Use the <GridFilterListIcon color="primary" aria-hidden="true" sx={{verticalAlign: 'text-bottom'}} /> <Typography component="span" color="primary"><strong>FILTERS</strong></Typography> button to specify search criteria:
                <ol>
                  <li>Select a Column (e.g., Client ID or Snapshot Date)</li>
                  <li>Specify a comparison operator. <br />For example, <i>contains</i> for a text column like Client ID would find rows where the string entered is included anywhere in the value.<br />For date fields like the Snapshot Date, the operator allows you to specify the type of date comparison, such as <i>is before</i>, which will find all snapshots with dates prior to the selected date.</li>
                  <li>Enter the value to use for the comparison (e.g., <i>hcrc</i> to find snapshots associated with the client u_of_hcrc.)</li>
                  <li>If desired, use the <GridAddIcon color="primary" aria-hidden="true" sx={{verticalAlign: 'text-bottom'}}/> <Typography component="span" color="primary"><strong>ADD FILTER</strong></Typography> button to specify additional criteria.<br />Select <i>And</i> for subsequent filters if <strong>all</strong> conditions should be met to display a row.<br />Select <i>Or</i> if the row should be displayed if it meets <strong>any</strong> condition.</li>
                  <li>To remove a filter, click the <CloseIcon aria-label="Delete" sx={{verticalAlign: 'text-bottom' }}/> button next to the condition</li>
                  <li>To remove all filters, click the <DeleteIcon color="primary" aria-hidden="true" sx={{verticalAlign: 'text-bottom' }}/> <Typography component="span" color="primary"><strong>REMOVE ALL</strong></Typography> button at the bottom of the filters modal.</li>
                </ol>
              </li></ul>
              </li>
              <li>Update the sort order of the snapshots<br/>
                  
                <ol>
                  <li>Hover your mouse over the column to sort<br /><i>Note: Columns containing only buttons do not support sorting.</i></li>
                  <li>A <GridArrowUpwardIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom'}}  /> sort icon will appear<br /><i>Note: If the column is currently sorted, the ascending or descending order sort icon is always displayed to indicate its state.</i></li>
                  <li>Click the sort icon to toggle its state. The state cycles between three options:
                    <ul>
                      <li>Unsorted (default state)</li>
                      <li><GridArrowUpwardIcon aria-label="Sort Ascending Icon" sx={{verticalAlign: 'text-bottom'}}  /> indicates that a column is currently sorted in ascending order</li>
                      <li><GridArrowDownwardIcon aria-label="Sort Descending Icon" sx={{verticalAlign: 'text-bottom'}}  /> indicates that a column is currently sorted in descending order</li>
                    </ul>
                  </li>
                </ol>
              </li>
              <li>View when the snapshot statuses were last synced to your device and refresh the list.
                  <ul>
                    <li>The timestamp at the bottom of the list of snapshots indicates when the snapshot data was last synced.</li>
                    <li>Click <CachedIcon aria-label="Refresh Snapshot List icon" sx={{verticalAlign: 'text-bottom'}}/> to refresh the list.</li>
                  </ul>
              </li>
            </ol>
          
        </Typography>
        {renderBackToTop()}
        <Typography variant='h6' sx={headerStyle}>
          Add Snapshot
        </Typography>

        <Typography variant='body1' component='div' sx={{ lineHeight: 2 }}>
        <Alert severity="info" color="error" variant="outlined" sx={{ mb: 2, maxWidth: '600px' }}>The client must exist prior to beginning this step. If this is a new client that is not yet configured, first <Link href="#client_add">
          <Typography variant="body2" sx={{ textDecoration: 'underline' }} component="span">
            add the client
          </Typography></Link>, then return to these instructions to continue.</Alert>
          Follow these steps to add a new snapshot.          
          <ol>
            <li>Click the <SchoolIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom', marginRight: '10px' }}/><strong>AMA</strong> menu item to navigate to the AMA dashboard.</li>
            <li>On the <strong>Snapshots</strong> tab, click the <AddCircleIcon color="primary" aria-label="Add Snapshot" sx={{verticalAlign: 'text-bottom', marginRight: '10px'}} />floating action button (bottom right of grid) to open the Snapshot Details view.</li>
            <li>Add snapshot details:
              <ul>
                  <li><strong>Client ID</strong>: (required) Select the client from the dropdown list</li>
                  <li><strong>Snapshot Date</strong>: (required) Enter the date (YYYY/MM/DD format), or click the <CalendarIcon sx={{verticalAlign: 'text-bottom' }} aria-label="Choose date"/> icon to select a date from the calendar</li>
                  <Alert severity="warning" sx={{ my: 2, maxWidth: '600px' }}>Once saved, these details cannot be edited. Be sure to confirm your selections before saving.</Alert>
              </ul>
            </li>
            <li>Upload Snapshot Files:<br/>
              <Alert severity="info" sx={{ my: 2, maxWidth: '600px' }}>To process a snapshot&apos;s data transformation, each file defined in the manifest must be present; however, <strong>it is not necessary to upload all files together</strong>. A snapshot can be created, and you may return to the snapshot to upload additional files as you receive them.</Alert>
              Once a client is selected, each item defined in that client&apos;s manifest appears with a file uploader box.  
              For each file that is ready for upload:                          
              <ol>
                <li>Drag the file to its corresponding manifest box.  Or, if preferred, click the <CloudUploadIcon color="primary" aria-hidden="true" sx={{verticalAlign: 'text-bottom' }}/> upload icon within the manifest box and select the file from the File Explorer.<br /><i>Note: Files must be comma-delimited (csv) text files or Excel files.  If an invalid file is uploaded, an alert box will pop up indicating the reason.</i></li>
                <li>If accepted, the manifest box updates to indicate that the file is <i>Ready for upload</i>.  If necessary, cancel the upload by clicking the <DeleteIcon color="primary" aria-label="Remove from Upload" sx={{verticalAlign: 'text-bottom' }} /> button.</li>                
              </ol>
            </li>
            <li>
              Click <strong>SAVE</strong> to save the snapshot or <strong>SAVE AND UPLOAD</strong> if manifest files were selected to upload.  This action will take you back to the snapshots list view after the AWS resources are successfully provisioned and any selected files are uploaded. (or click <strong>CANCEL</strong> to discard changes).
              <Alert severity="info" sx={{ my: 2, maxWidth: '600px' }}>When you save a snapshot configuration, back-end AWS resources are automatically provisioned, and any uploaded files go through initial transformations.<br />Once the files are processed, a notification email is sent to all email addresses configured for the Client <Link href="#client_add">
          <Typography variant="body2" sx={{ textDecoration: 'underline' }} component="span">
            notification recipients
          </Typography></Link>.<br /><br /><i>Note: The snapshot will not display a file status until the uploaded file completes processing. Therefore, when viewing a snapshot where files were recently uploaded, it may appear that files are not yet uploaded. The file&apos;s status will be reflected once processing is complete.</i></Alert>
            </li>
          </ol>
          
        </Typography>
        {renderBackToTop()}
        <Typography id="snapshot_add_files" variant='h6' sx={headerStyle}>
          Add Files to Existing Snapshot
        </Typography>

        <Typography variant='body1' component='div' sx={{ lineHeight: 2 }}>
          Follow these steps to upload additional files to an existing snapshot.
          <ol>
            <li>Click the <SchoolIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom', marginRight: '10px' }}/><strong>AMA</strong> menu item to navigate to the AMA dashboard.</li>
            <li>On the <strong>Snapshots</strong> tab while viewing the list of snapshots, click the <CloudUploadIcon aria-label="Upload Files to Bronze Icon" sx={{verticalAlign: 'text-bottom' }}/> button on the snapshot row you want to modify.</li>
            <li>Add one or more additional files
            <Alert severity="info" sx={{ my: 2, maxWidth: '600px' }}>Previously collected files cannot be edited, and these are indicated with the <CloudDoneIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom', margin: '0 5px'}}/> <strong>Bronze collected</strong> status or, in the case of an error, the <ErrorOutlineIcon aria-hidden="true" sx={{ color: theme.palette.error.main, verticalAlign: 'text-bottom' }}/> <strong>error</strong> status will appear. When there is a file error, the cause is provided in the tooltip, which is visible when hovering over the error icon.<br /><br /><i>Note: If a file has errored (e.g., due to the file not matching the manifest definition), the only corrective measure is to delete the current snapshot and upload the files in a new snapshot.</i></Alert>
            </li>
            <li>Click <strong>UPLOAD</strong> to complete the file uploads and return to the snapshots list view (or click <strong>CANCEL</strong> to discard changes).</li>
          </ol>
        </Typography>
        {renderBackToTop()}
        <Typography id="snapshot_delete" variant='h6' sx={headerStyle}>
          Delete Snapshot
        </Typography>

        <Typography variant='body1' component='div' sx={{ lineHeight: 2 }}>
          Follow these steps to delete an existing snapshot.
          <ol>
            <li>Click the <SchoolIcon aria-hidden="true" sx={{verticalAlign: 'text-bottom', marginRight: '10px' }}/><strong>AMA</strong> menu item to navigate to the AMA dashboard.</li>
            <li>On the <strong>Snapshots</strong> tab while viewing the list of snapshots, click the <DeleteIcon sx={{verticalAlign: 'text-bottom' }} aria-label="Delete Icon" /> button on the snapshot row you want to delete.</li>
            <li>
              Click <strong>OK</strong> on the modal confirmation window.
              <Alert severity="warning" sx={{ my: 2, maxWidth: '600px' }}>Be very careful when deleting snapshots. <strong>All files from bronze and silver associated with the snapshot will be permanently deleted.</strong></Alert>
            </li>
          </ol>
        </Typography>
       
      </Stack>
    );
  };

  const renderDefinitions = () => {
    return (
      <Stack spacing={3} sx={{ py: 2 }}>

        <TableContainer component={Paper} sx={{margin: 'auto' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>ETL Pipeline</strong></TableCell>
                <TableCell>An ETL (Extract, Transform, Load) Pipeline in the context of AWS Glue refers to the process of extracting data from various sources, transforming it into a desired format, and loading it into a target destination. AWS Glue ETL pipelines automate these tasks, making it efficient to process and prepare data for analysis, reporting, or storage. It simplifies the ETL process by providing managed services for data cataloging, schema evolution, and job execution, allowing users to focus on data transformation logic rather than infrastructure management.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>Data Lake</strong></TableCell>
                <TableCell>A Data Lake is a centralized and scalable repository that allows organizations to store vast amounts of raw and processed data in its native format.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>Data Warehouse</strong></TableCell>
                <TableCell>A Data Warehouse is a centralized and integrated repository of structured, historical data collected from various sources within an organization. It is designed for the purpose of supporting business intelligence, reporting, and analytical processes. Data Warehouses organize and store data in a way that facilitates efficient querying and analysis, often using a multidimensional model. This centralized data storage allows businesses to make informed decisions by providing a consolidated view of historical and current data from different departments or systems within the organization. Data Warehouses play a crucial role in supporting data-driven decision-making and business intelligence initiatives.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>AWS Glue</strong></TableCell>
                <TableCell>AWS Glue is a fully managed extract, transform, and load (ETL) service provided by Amazon Web Services (AWS). It simplifies the process of preparing and loading data for analytics by automating tasks such as data cataloging, schema inference, and job execution. AWS Glue is designed to work with a variety of data sources, including data lakes, databases, and data warehouses. It provides a serverless environment, allowing users to focus on defining ETL transformations without the need for infrastructure management. AWS Glue also includes a centralized metadata catalog, making it easy to discover, understand, and manage data assets across different storage locations and formats.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>AWS Glue Job</strong></TableCell>
                <TableCell>An AWS Glue Job is a serverless ETL (Extract, Transform, Load) process provided by Amazon Web Services (AWS) through the AWS Glue service. It allows users to define and execute ETL workflows for processing and transforming data from various sources into a desired format for analysis, reporting, or storage. AWS Glue Jobs automate the steps involved in data preparation, cleansing, and transformation. These jobs can be scheduled to run at specified intervals or triggered based on events, providing flexibility for managing data workflows. AWS Glue Jobs leverage a scalable and fully managed infrastructure, enabling users to focus on defining transformations and business logic rather than infrastructure management.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>AWS Glue Notebook</strong></TableCell>
                <TableCell>An AWS Glue Notebook is an interactive development environment provided by Amazon Web Services (AWS) as part of the AWS Glue service. It allows data engineers, data scientists, and analysts to interactively write, run, and debug code for ETL (Extract, Transform, Load) tasks.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>AWS Glue Crawler</strong></TableCell>
                <TableCell>An AWS Glue Crawler is a component of the AWS Glue service that automatically discovers, classifies, and registers metadata about the data in various sources. The Crawler scans and analyzes data stores, such as Amazon S3, databases, or data warehouses, to identify the structure, format, and schema of the data. Once the Crawler has performed its analysis, it creates metadata tables in the AWS Glue Data Catalog, allowing users to easily access and query the data.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>AWS Glue Data Catalog</strong></TableCell>
                <TableCell>The AWS Glue Data Catalog is a fully managed metadata repository provided by Amazon Web Services (AWS) as part of the AWS Glue service. It serves as a central hub for storing and managing metadata about data assets, making it easier to discover, understand, and govern data within an organization.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>Apache Spark</strong></TableCell>
                <TableCell>
                  Apache Spark is an open-source, distributed computing system designed for large-scale data processing and analytics. It provides a fast and general-purpose cluster computing framework that supports various programming languages, including Java, Scala, Python, and R. Spark is known for its in-memory processing capabilities, making it well-suited for iterative algorithms and interactive data analysis.
                  Key features of Apache Spark include:
                  <ul>
                    <li>Speed: Spark aims to process data quickly through in-memory computation, reducing the need for repetitive disk I/O.</li>
                    <li>Ease of Use: Spark provides high-level APIs in multiple languages, making it accessible to a broad audience, including data engineers, data scientists, and analysts.</li>
                    <li>Versatility: Spark supports various workloads, including batch processing, interactive queries, streaming, and machine learning, all within a unified framework.</li>
                    <li>Fault Tolerance: Spark provides fault tolerance through lineage information, allowing it to recover lost data due to node failures.</li>
                    <li>Ease of Integration: Spark can integrate with various data sources, including Hadoop Distributed File System (HDFS), Apache Hive, Apache HBase, and more.</li>
                    <li>Extensibility: Spark includes libraries for SQL, machine learning (MLlib), graph processing (GraphX), and stream processing (Spark Streaming).</li>
                  </ul>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>AWS VPC</strong></TableCell>
                <TableCell>Amazon Virtual Private Cloud (Amazon VPC) is a service provided by Amazon Web Services (AWS) that enables users to launch and configure a logically isolated section of the AWS Cloud. Within a VPC, users have control over aspects such as IP address ranges, subnets, routing tables, and security settings, creating a customizable and secure environment for deploying AWS resources.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>Workflow</strong></TableCell>
                <TableCell>Workflow generically refers to the sequencing and orchestration of specific steps or operations in an ETL pipeline.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>Serverless</strong></TableCell>
                <TableCell>Serverless computing is a cloud computing execution model where cloud providers automatically manage the infrastructure for running applications. In a serverless architecture, developers focus on writing code (functions) that respond to events or triggers, and the cloud provider takes care of provisioning, scaling, and maintaining the underlying infrastructure.</TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>Data Stage: Bronze</strong></TableCell>
                <TableCell>The initial stage in a data processing pipeline where raw or minimally processed data is ingested into a data lake or storage system</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>Data Stage: Silver</strong></TableCell>
                <TableCell>The Silver stage focuses on improving data quality, making it more reliable for downstream analytics and reporting. Data is standardized to ensure consistency in format and structure. Data at the Silver stage must adhere to AMA Core Schema.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ verticalAlign: 'top'}}><strong>Data Stage: Gold</strong></TableCell>
                <TableCell>The Gold stage delivers high-quality, refined data for informed decision-making and strategic analysis. Data may be enriched with additional attributes and insights. Aggregations and summarizations may be applied to support high-level reporting.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
       
      </Stack>
    );
  };

  const renderFAQs = () => {
    return (
      <Stack spacing={3} sx={{ py: 2 }}>

        <Typography variant='h6' sx={headerStyle}>
          Coming soon!
        </Typography>
       
      </Stack>
    );
  };

  const sections : HelpSection[] = [
    
    { id: '1', title: 'ETL 101', content: renderEtl101Section() },
    { id: '2', title: 'AWS Glue ETL', content: renderAwsGlueEtlSection() },
    { id: '3', title: 'AMA: Getting Started', content: renderAmaGettingStarted() },
    { id: '4', title: 'AMA: Configuring Clients', content: renderAmaConfiguringClients() },
    { id: '5', title: 'AMA: Managing Snapshots', content: renderAmaManagingSnapshots() },    
    { id: '6', title: 'Definitions', content: renderDefinitions() },
    { id: '7', title: 'FAQs', content: renderFAQs() },
  ];

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      {/* <Paper sx={{ height: '100%', }}> */}
        <Stack id="top" spacing={4} sx={{ mx: 4 }}>
          
          {/* TOC */}
          <Typography  variant="h4" sx={{ pt: 2 }} gutterBottom>
            Topics
          </Typography>
          
          <List sx={{ px: 2 }}>
            {sections.map((section) => (
              <ListItem 
                key={section.id} 
                onClick={() => scrollToSection(section.id)} 
                sx={{ cursor: 'pointer', margin: 0, mb: 1, padding: 0, '&:hover': { textDecoration: 'underline' }, textAlign:'left' }}>
                <ListItemText primary={`${section.id}.  ${section.title}`} sx={{ cursor: 'pointer', margin: 0, padding: 0 }} />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ marginTop: 10 }} />

          {/* Content Sections */}
          {sections.map((section) => (
            <Box key={section.id} id={section.id} sx={{ marginTop: 2 }}>
              <Typography variant="h4" sx={{ my: 2 }}>
                {section.title}
              </Typography>
              {section.content}
              {renderBackToTop()}
              <Divider sx={{ marginTop: 4 }} />
            </Box>
          ))}

        </Stack>
      {/* </Paper> */}
    </Box>
  );
}

{/* <Box sx={{ width: '100%', height: '100%', border: 'solid 1px black', overflow: 'auto' }}>
  <Box id="inner" sx={{ display: 'flex', flexDirection: 'column', width: '100%', border: 'solid 1px orange' }}>
    {Array.from({ length: 20 }, (_, index) => (
      <Typography key={index} component="h4" variant="h6" sx={{ color: theme.palette.grey[400], fontSize: '1rem'}}>
        Silver Core Schema
      </Typography>
    ))}
  </Box>
</Box> */}

